import { useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { useTran } from "@/hooks";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { User, Mail, Eye, EyeOff } from "lucide-react";
import { useAuthStore } from "../hooks";
import { RegisterSchema } from "../schemas";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/common";
import { PATHS } from "@/lib/router";
import { useCompanyStore } from "@/features/company";
import { cn } from "@/lib/utils";

export const RegisterForm = observer(({ compact = false }) => {
  const navigate = useNavigate();
  const { t } = useTran();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { registerForm, loading, register } = useAuthStore();
  const { clearDraft, reset, setCompany } = useCompanyStore();

  const handleRegister = async (values, { setSubmitting }) => {
    const payload = { ...values, role: values.purpose };
    const success = await register(payload);
    setSubmitting(false);

    if (success) {
      clearDraft();
      reset({ persist: false });
      setCompany(null);
      navigate(PATHS.COMPANY_SETUP, {
        replace: true,
        state: { from: "register" },
      });
    }
  };

  return (
    <Formik
      initialValues={{
        name: "",
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        purpose: "",
      }}
      validationSchema={RegisterSchema}
      onSubmit={handleRegister}
    >
      {({ isSubmitting, errors, touched, values, handleChange }) => (
        <Form
          className={cn(
            "mx-auto w-full px-1 sm:px-2",
            compact
              ? "max-w-[340px] space-y-2 md:px-1"
              : "max-w-md space-y-3 md:px-4 lg:px-6",
          )}
        >
          <h1
            className={cn(
              "text-center font-semibold text-foreground",
              compact ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl",
            )}
          >
            {t("auth.register.title")}
          </h1>

          <p
            className={cn(
              "text-center text-muted-foreground",
              compact ? "text-xs" : "text-sm",
            )}
          >
            {t("auth.register.subtitle")}
          </p>

          {/* Display Name */}
          <FormField
            name="name"
            placeholder={t("auth.register.displayName")}
            value={values.name}
            onChange={handleChange}
            error={touched.name && errors.name}
            rightIcon={<User className="h-5 w-5" />}
            className="space-y-1"
          />

          {/* Username */}
          <FormField
            name="username"
            placeholder={t("auth.register.username")}
            value={values.username}
            onChange={handleChange}
            error={touched.username && errors.username}
            rightIcon={<User className="h-5 w-5" />}
            className="space-y-1"
          />

          {/* Email */}
          <FormField
            name="email"
            type="email"
            placeholder={t("auth.register.email")}
            value={values.email}
            onChange={handleChange}
            error={touched.email && errors.email}
            rightIcon={<Mail className="h-5 w-5" />}
            className="space-y-1"
          />

          {/* Password */}
          <FormField
            name="password"
            type={showPassword ? "text" : "password"}
            placeholder={t("auth.register.password")}
            value={values.password}
            onChange={handleChange}
            error={touched.password && errors.password}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            }
            className="space-y-1"
            rightIconClassName="top-[60%]"
          />

          {/* Confirm Password */}
          <FormField
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            placeholder={t("auth.register.confirmPassword")}
            value={values.confirmPassword}
            onChange={handleChange}
            error={touched.confirmPassword && errors.confirmPassword}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            }
            className="space-y-1"
            rightIconClassName="top-[60%]"
          />

          {/* Purpose of Use */}
          <div className="space-y-2">
            <p
              className={cn(
                "font-medium text-foreground",
                compact ? "text-xs" : "text-sm",
              )}
            >
              {t("auth.register.purposeOfUse")}
            </p>
            <div
              className={cn(
                "space-y-2 rounded-2xl border border-border/80 bg-muted/20",
                compact ? "p-2.5" : "p-3",
              )}
            >
              <label
                className={cn(
                  "flex cursor-pointer items-center gap-2 text-foreground",
                  compact ? "text-xs" : "text-sm",
                )}
              >
                <Field
                  type="radio"
                  name="purpose"
                  value="super_admin"
                  className="h-4 w-4"
                />
                <span>{t("auth.register.forMyBusiness")}</span>
              </label>
              <label
                className={cn(
                  "flex cursor-pointer items-center gap-2 text-foreground",
                  compact ? "text-xs" : "text-sm",
                )}
              >
                <Field
                  type="radio"
                  name="purpose"
                  value="solution_provider"
                  className="h-4 w-4"
                />
                <span>{t("auth.register.solutionProvider")}</span>
              </label>
              <label
                className={cn(
                  "flex cursor-pointer items-center gap-2 text-foreground",
                  compact ? "text-xs" : "text-sm",
                )}
              >
                <Field
                  type="radio"
                  name="purpose"
                  value="reseller"
                  className="h-4 w-4"
                />
                <span>{t("auth.register.reseller")}</span>
              </label>
            </div>
            <ErrorMessage name="purpose">
              {(msg) => <p className="text-xs text-red-600">{t(msg)}</p>}
            </ErrorMessage>
          </div>

          {/* Server Error */}
          {registerForm?.serverError && (
            <p className="text-xs text-red-600">{registerForm.serverError}</p>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting || loading}
            size={compact ? "sm" : "default"}
            className="w-full rounded-full bg-primary hover:bg-primary/90"
          >
            {isSubmitting || loading
              ? t("auth.register.registering")
              : t("auth.register.submit")}
          </Button>
        </Form>
      )}
    </Formik>
  );
});
